import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HeroEntity, ItemEntity } from '@generated/gql';
import { ItemEntityExtendedType } from '@models/item-entity-extended.type';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { DestroyService } from '@services/destroy.service';
import { SubgraphService } from '@services/graph/subgraph.service';
import { ItemControllerService } from '@services/onchain/item-controller.service';
import { DialogTitleComponent } from '@shared/components/dialog-title/dialog-title.component';
import { ItemDescriptionDialogComponent } from '@shared/components/item-description-dialog/item-description-dialog.component';
import { ItemSlotComponent } from '@shared/components/item-slot/item-slot.component';
import { LoadingSmallComponent } from '@shared/components/loading-small/loading-small.component';
import { ItemActionType } from '@shared/constants/inventory.constants';
import { MAIN_ROUTES } from '@shared/constants/routes.constant';
import { countItems } from '@shared/utils';
import { OTHER_ITEMS, prepareDataForOtherItems } from '@shared/utils/items-utils';
import { finalize, forkJoin, of, takeUntil } from 'rxjs';

@Component({
  selector: 'app-bottles-dialog',
  standalone: true,
  templateUrl: './bottles-dialog.component.html',
  styleUrls: ['./bottles-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  host: {
    class: 'app-window-responsive-background g-flex-column',
  },
  imports: [DialogTitleComponent, ItemSlotComponent, TranslatePipe, TranslateDirective, LoadingSmallComponent],
})
export class BottlesDialogComponent implements OnInit {
  items: ItemEntity[] = [];
  loading = true;

  constructor(
    @Inject(DIALOG_DATA)
    public data: {
      chainId: number;
      account: string;
      heroToken: string;
      heroTokenId: number;
      hero: HeroEntity;
    },
    private dialogRef: DialogRef<null, BottlesDialogComponent>,
    private dialog: Dialog,
    private changeDetectorRef: ChangeDetectorRef,
    private itemControllerService: ItemControllerService,
    private subgraphService: SubgraphService,
    private router: Router,
    private destroy$: DestroyService,
  ) {}

  ngOnInit(): void {
    this.loadItems();
  }

  close(): void {
    this.dialogRef.close(null);
  }

  private loadItems() {
    forkJoin({
      cons:
        this.data.hero.sandBoxMode === 1
          ? this.subgraphService.heroSandboxItems$(`${this.data.heroToken}-${this.data.heroTokenId}`)
          : this.subgraphService.usersItems$(this.data.account, 1000, true),
      other: this.data.hero.sandBoxMode === 1 ? of([]) : this.subgraphService.userOtherItemsUse$(this.data.account),
    })
      .pipe(
        finalize(() => {
          this.changeDetectorRef.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ cons, other }) => {
        const values = [...cons, ...other];
        const count = countItems(values);

        let items = [].map(i => i as ItemEntityExtendedType); // this.items.map(i => i as ItemEntityExtendedType).filter(it => it.meta.isConsumableItem);

        Object.keys(count).forEach(key => {
          const found = values.find(it => it.meta.name === key);
          if (found) {
            items = [
              ...items,
              { ...found, count: count[key], countFormatted: count[key] > 99 ? '99+' : String(count[key]) },
            ];
          }
        });
        this.items = items;
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  openItemDescriptionDialog(item?: ItemEntityExtendedType): void {
    if (!item) {
      return;
    }

    const dialogRef: DialogRef<ItemActionType, ItemDescriptionDialogComponent> | undefined = this.dialog.open(
      ItemDescriptionDialogComponent,
      {
        panelClass: 'app-overlay-pane',
        data: { item, isShowButtons: true, isCanUse: true, slot: 0, chainId: this.data.chainId, hero: this.data.hero },
      },
    );

    dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data?.useItem) {
        this.use(data.useItem);
      } else if (data?.useOtherItem) {
        this.useOtherItem(data.useOtherItem);
      }
    });
  }

  use(item: ItemEntity) {
    this.dialogRef.close(null);

    this.changeDetectorRef.markForCheck();
    this.itemControllerService
      .use$(this.data.account, this.data.chainId, item.meta.id, item.itemId, this.data.heroToken, this.data.heroTokenId)
      .pipe(
        finalize(() => {
          this.changeDetectorRef.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
  }

  useOtherItem(item: ItemEntity) {
    const data = prepareDataForOtherItems(item.meta, this.data.heroToken, this.data.heroTokenId);
    if (data) {
      this.dialogRef.close(null);
      this.changeDetectorRef.markForCheck();
      this.itemControllerService
        .useOtherItem$(this.data.account, this.data.chainId, item.meta.id, item.itemId, data)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (item.meta.name === OTHER_ITEMS.exitFromDungeon) {
            // this.router.navigate([MAIN_ROUTES.HERO, this.data.heroToken, this.data.heroTokenId.toString()]);
            this.router.navigate([MAIN_ROUTES.MAIN, MAIN_ROUTES.HERO_CAMP]);
          }
        });
    }
  }
}
